import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { useLang } from "../contexts/LangContext";
import { getLowPlacesThreshold, isLowPlaces } from "../lib/lowPlaces";
import api from "../lib/api";
import { Calendar as CalIcon, Users } from "lucide-react";

const CATS = ["all", "vetements", "accessoires", "stages", "evenements"];

export default function Boutique() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const initialCat = CATS.includes(params.get("cat")) ? params.get("cat") : "all";
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(initialCat);

  useEffect(() => {
    const opts = category === "all" ? {} : { params: { category } };
    api.get("/products", opts).then((r) => setProducts(r.data)).catch(() => {});
    // Keep URL in sync with the selected category (?cat=stages)
    const next = new URLSearchParams(params);
    if (category === "all") next.delete("cat"); else next.set("cat", category);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Group products by category when "all" selected
  const grouped = useMemo(() => {
    if (category !== "all") return { [category]: products };
    return products.reduce((acc, p) => {
      (acc[p.category] = acc[p.category] || []).push(p);
      return acc;
    }, {});
  }, [products, category]);

  const sectionTitle = (cat) =>
    cat === "vetements" || cat === "accessoires" ? null
    : cat === "stages" ? t.shop.sectionStages
    : cat === "evenements" ? t.shop.sectionEvents
    : "";

  return (
    <div data-testid="shop-page">
      <PageHeader eyebrow={t.shop.eyebrow} title={t.shop.title} lead={t.shop.lead} />

      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          {/* Categories */}
          <div className="flex flex-wrap gap-2 border-b border-slate-200 mb-12" data-testid="shop-categories">
            {CATS.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${category === c ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
                data-testid={`shop-cat-${c}`}
              >
                {t.shop.categories[c]}
              </button>
            ))}
          </div>

          {/* Sections */}
          {category === "all" ? (
            <div className="space-y-16">
              <Section
                title={t.shop.sectionVet}
                products={[...(grouped.vetements || []), ...(grouped.accessoires || [])]}
                t={t} lang={lang}
              />
              {grouped.stages && grouped.stages.length > 0 && (
                <Section title={t.shop.sectionStages} products={grouped.stages} t={t} lang={lang} />
              )}
              {grouped.evenements && grouped.evenements.length > 0 && (
                <Section title={t.shop.sectionEvents} products={grouped.evenements} t={t} lang={lang} />
              )}
            </div>
          ) : (
            <Section
              title={sectionTitle(category)}
              products={products}
              t={t} lang={lang}
            />
          )}
        </div>
      </section>
    </div>
  );
}

function Section({ title, products, t, lang }) {
  if (!products || products.length === 0) return null;
  return (
    <div>
      {title && (
        <div className="mb-8">
          <h2 className="font-serif text-3xl text-navy">{title}</h2>
          <div className="divider-gold mt-3" />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {products.map((p) => <ProductCard key={p.id} p={p} t={t} lang={lang} />)}
      </div>
    </div>
  );
}

function ProductCard({ p, t, lang }) {
  const { content } = useLang();
  const isStage = p.type === "stage" || p.type === "event";
  const placesLeft = isStage ? (p.max_places || 0) - (p.places_taken || 0) : null;
  const isFull = isStage && (p.stage_status === "full" || placesLeft <= 0);
  const isClosed = isStage && p.stage_status === "closed";
  const isLow = isStage && isLowPlaces({ places: p.max_places, taken: p.places_taken, isFull, isClosed, threshold: getLowPlacesThreshold(content) });

  return (
    <Link to={`/shop/${p.id}`} className="group" data-testid={`product-card-${p.id}`}>
      <div className="aspect-square overflow-hidden bg-slate-100 relative">
        {p.image ? (
          <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy/5 to-club-yellow/10">
            <span className="text-xs uppercase tracking-widest text-slate-400">{p.name.substring(0, 3)}</span>
          </div>
        )}
        {isStage && (
          <div className={`absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${isFull ? "bg-club-red text-white" : isClosed ? "bg-slate-500 text-white" : isLow ? "bg-club-red text-white animate-pulse" : "bg-club-yellow text-navy"}`} data-testid={isLow ? `shop-last-places-${p.id}` : undefined}>
            {isFull ? t.shop.stageFull : isClosed ? t.shop.stageClosed : isLow ? t.stages.lastPlaces : t.shop.stageOpen}
          </div>
        )}
      </div>
      <div className="pt-4">
        <div className="text-[10px] uppercase tracking-widest text-club-yellow-dark mb-1 font-semibold">{t.shop.categories[p.category] || p.category}</div>
        <h3 className="font-serif text-xl text-navy group-hover:text-club-red transition-colors">{p.name}</h3>
        {isStage && p.start_date && (
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <CalIcon className="w-3 h-3 text-club-yellow-dark" />
            {new Date(p.start_date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "2-digit", month: "short" })}
            {p.end_date && p.end_date !== p.start_date && ` → ${new Date(p.end_date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "2-digit", month: "short" })}`}
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="font-serif text-lg text-navy">€ {p.price.toFixed(2)}</span>
          {isStage ? (
            <span className={`text-[10px] uppercase tracking-widest flex items-center gap-1 ${isLow ? "text-club-red font-semibold" : "text-slate-600"}`}>
              <Users className="w-3 h-3" />
              {isFull ? t.shop.stageFull : `${placesLeft} ${t.shop.placesLeft}`}
            </span>
          ) : (
            <span className={`text-[10px] uppercase tracking-widest ${p.stock > 0 ? "text-green-700" : "text-red-600"}`}>
              {p.stock > 0 ? t.shop.inStock : t.shop.outOfStock}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
